import { useCallback, useState } from "react";

type NasState = "idle" | "mounting" | "mounted" | "error";

type NasCredentials = {
  username: string;
  password: string;
  serverPath?: string;
  mountPoint?: string;
};

function isWindows() {
  return typeof navigator !== "undefined" && /Windows/i.test(navigator.userAgent);
}

function validateWindowsMountPoint(mountPoint: string | undefined) {
  if (!mountPoint) return "Mount point is required.";
  const trimmed = mountPoint.trim();
  if (/^[A-Za-z]:?$/.test(trimmed)) return null;
  if (/[\\/]/.test(trimmed)) {
    return "On Windows, use a drive letter like Z: (no folder paths).";
  }
  return "Invalid mount point. Use a drive letter like Z:.";
}

export function useNasMount(defaultMountPoint?: string) {
  const [status, setStatus] = useState<NasState>("idle");
  const [mountPoint, setMountPoint] = useState<string | undefined>(defaultMountPoint);
  const [error, setError] = useState<string | null>(null);

  const mount = useCallback(
    async (credentials: NasCredentials) => {
      const effectiveMountPoint = credentials.mountPoint || defaultMountPoint;
      const effectiveServerPath = credentials.serverPath?.trim();
      if (!effectiveMountPoint) {
        setStatus("error");
        setError("Mount point is required.");
        return;
      }
      if (!effectiveServerPath) {
        setStatus("error");
        setError("Server path is required.");
        return;
      }
      if (isWindows()) {
        const validationMessage = validateWindowsMountPoint(effectiveMountPoint);
        if (validationMessage) {
          setStatus("error");
          setError(validationMessage);
          return;
        }
      }
      setStatus("mounting");
      setError(null);

      try {
        if (!window.api?.mountNas) {
          setStatus("mounted");
          return;
        }
        const result = await window.api.mountNas({
          username: credentials.username,
          password: credentials.password,
          serverPath: effectiveServerPath,
          mountPoint: effectiveMountPoint
        });
        if (result.ok) {
          setStatus("mounted");
          setMountPoint(result.mountPoint);
        } else {
          if (result.code === "CREDENTIALS_REQUIRED") {
            // Keep modal open without surfacing an error banner.
            setStatus("idle");
            setError(null);
          } else {
            setStatus("error");
            setError(result.message || "Mount failed");
          }
        }
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    },
    [defaultMountPoint]
  );

  return { status, mountPoint, error, mount };
}
